import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  Card,
  CardMedia,
  CircularProgress,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import ModalMAIQ from 'app/components/ModalMAIQ';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import PublishIcon from '@mui/icons-material/Publish';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import { formatDateForDisplay } from 'utils/dateUtils';
import { OfferFormData, OfferFormErrors, validateOfferForm, hasValidationErrors } from './components/OfferForm';
import OfferForm from './components/OfferForm';

export interface OfferSaveResult {
  success: boolean;
  message?: string;
}

interface OfferDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  onSave: (payload: any) => Promise<OfferSaveResult>;
  onDelete: () => void;
  onPublish?: () => Promise<void>;
  offerType: 'lease' | 'leaseFinance' | 'finance';
}

const OfferDetailsModal: React.FC<OfferDetailsModalProps> = ({
  open,
  onClose,
  data,
  onSave,
  onDelete,
  onPublish,
  offerType,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openPublishConfirm, setOpenPublishConfirm] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [openSavePublishedConfirm, setOpenSavePublishedConfirm] = useState(false);
  const [formErrors, setFormErrors] = useState<OfferFormErrors>({});
  const [apiError, setApiError] = useState<string>('');
  const [form, setForm] = useState<OfferFormData>({
    headline: '',
    description: '',
    content: '',
    leaseLabel: 'Lease For',
    leaseDownLabel: 'Down',
    financeLabel: 'Finance From',
    leaseAmount: '',
    term: '',
    leaseTerm: '',
    leaseDown: '',
    financeRate: '',
    financeTerm: '',
    startDate: '',
    endDate: '',
    year: '',
    make: '',
    model: '',
    trim: '',
    drivetrain: '',
    offerUrl: '',
    offerImageUrl: '',
    disclaimer: '',
  });

  useEffect(() => {
    if (data) {
      // Map API response to form fields
      const leaseTerms = data.lease_terms?.[0] || {};
      const financeTerms = data.finance_terms || {};

      setForm({
        headline: data.headline || '',
        description: data.description || '',
        content: data.content || '',
        leaseLabel: leaseTerms.lease_label || 'Lease For',
        leaseDownLabel: leaseTerms.lease_down_label || 'Down',
        financeLabel: financeTerms.finance_label || 'Finance From',
        leaseAmount: leaseTerms.monthly_payment?.toString() || '',
        term: leaseTerms.lease_term_months?.toString() || '',
        leaseTerm: leaseTerms.lease_term_months?.toString() || '',
        leaseDown: leaseTerms.due_at_signing?.toString() || '',
        financeRate: financeTerms.apr?.toString() || '',
        financeTerm: financeTerms.term_months?.toString() || '',
        startDate: data.start_date || '',
        endDate: data.end_date || '',
        year: data.vehicle_year?.toString() || '',
        make: data.vehicle_make || '',
        model: data.vehicle_model || '',
        trim: data.trim || '',
        drivetrain: data.drivetrain || '',
        offerUrl: data.offer_url || '',
        offerImageUrl: data.offer_image_url || '',
        disclaimer: data.disclaimer || '',
      });
    }
  }, [data]);

  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setFormErrors({});
      setApiError('');
      setSaving(false);
      setOpenSavePublishedConfirm(false);
    }
  }, [open]);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (apiError) {
      setApiError('');
    }
    
    // Clear field-specific error when user starts typing
    if (formErrors[field as keyof OfferFormErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof OfferFormErrors];
        return newErrors;
      });
    }
  };

  const performSave = async (): Promise<boolean> => {
    setSaving(true);
    setApiError('');
    try {
      const payload = {
        headline: form.headline,
        description: form.description,
        content: form.content,
        start_date: form.startDate,
        end_date: form.endDate,
        vehicle_year: form.year,
        vehicle_make: form.make,
        vehicle_model: form.model,
        trim: form.trim,
        drivetrain: form.drivetrain,
        offer_url: form.offerUrl,
        offer_image_url: form.offerImageUrl,
        disclaimer: form.disclaimer,
        lease_terms: offerType === 'lease' || offerType === 'leaseFinance' ? [{
          monthly_payment: parseFloat(form.leaseAmount) || 0,
          lease_term_months: parseInt(offerType === 'lease' ? form.term : form.leaseTerm, 10) || 0,
          due_at_signing: parseFloat(form.leaseDown) || 0,
          lease_label: form.leaseLabel || 'Lease For',
          lease_down_label: form.leaseDownLabel || 'Down',
        }] : [],
        finance_terms: offerType === 'finance' || offerType === 'leaseFinance' ? {
          apr: parseFloat(form.financeRate) || 0,
          term_months: parseInt(form.financeTerm) || 0,
          finance_label: form.financeLabel || 'Finance From',
        } : {},
      };
      const result = await onSave(payload);
      if (result.success) {
        setIsEditing(false);
        setFormErrors({});
        setApiError('');
        return true;
      }
      setApiError(result.message || 'Failed to update offer. Please try again.');
      return false;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update offer. Please try again.';
      setApiError(message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = async () => {
    const errors = validateOfferForm(form, offerType);
    setFormErrors(errors);

    if (hasValidationErrors(errors)) {
      console.warn('Validation failed:', errors);
      return;
    }

    if (data?.status === 'PUBLISHED') {
      setOpenSavePublishedConfirm(true);
      return;
    }

    await performSave();
  };

  const getOfferTypeDisplayName = (type: string): string => {
    switch (type) {
      case 'lease':
        return 'Lease';
      case 'leaseFinance':
        return 'Lease and Finance';
      case 'finance':
        return 'Finance';
      default:
        return 'Offer';
    }
  };

  // Helper function to check if value should be displayed
  const shouldDisplayValue = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'number' && value === 0) return true; // Allow 0 for numbers
    return true;
  };

  // Helper function to get display value
  const getDisplayValue = (value: any, prefix: string = '', suffix: string = ''): string => {
    if (!shouldDisplayValue(value)) return '';
    return `${prefix}${value}${suffix}`;
  };

  // Helper function to truncate URL
  const truncateUrl = (url: string, maxLength: number = 50): string => {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    return `${url.substring(0, maxLength)}...`;
  };

  // Image Preview Component
  const ImagePreview = ({ imageUrl, isEditing, onClick }: { imageUrl: string; isEditing: boolean; onClick?: () => void }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    useEffect(() => {
      setImageError(false);
      setImageLoading(true);
    }, [imageUrl]);

    if (!imageUrl || imageUrl.trim() === '') {
      return (
        <Box
          sx={{
            width: isEditing ? 120 : 150,
            height: isEditing ? 80 : 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.100',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'grey.500' }}>
            <ImageIcon sx={{ fontSize: 32, mb: 0.5 }} />
            <Typography variant="caption" display="block">
              No Image
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Tooltip 
        title={truncateUrl(imageUrl, 60)} 
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 400,
              fontSize: '0.75rem',
              wordBreak: 'break-all'
            }
          }
        }}
      >
        <Box
          sx={{
            width: isEditing ? 120 : 150,
            height: isEditing ? 80 : 100,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'grey.300',
            position: 'relative',
            cursor: onClick ? 'pointer' : 'default',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': onClick ? {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            } : {}
          }}
          onClick={onClick}
        >
          {imageLoading && (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{ position: 'absolute', top: 0, left: 0 }}
            />
          )}
          {imageError ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'grey.100',
                color: 'grey.500',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <ImageIcon sx={{ fontSize: 24, mb: 0.5 }} />
                <Typography variant="caption" display="block">
                  Failed to load
                </Typography>
              </Box>
            </Box>
          ) : (
            <img
              src={imageUrl}
              alt="Offer"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: imageLoading ? 'none' : 'block',
              }}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageError(true);
                setImageLoading(false);
              }}
            />
          )}
          {onClick && !imageError && !imageLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: theme.palette.action.selected,
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s ease-in-out',
                '.MuiBox-root:hover &': {
                  opacity: 1
                }
              }}
            >
              <Typography variant="caption" sx={{ color: 'white', fontSize: '10px' }}>
                ↗
              </Typography>
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  };

  if (!data) return null;

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{ width: '800px', maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <Box
          className="modal-header"
          sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" fontWeight={600}>
            {getOfferTypeDisplayName(offerType)} Offer Details
          </Typography>
          <Box>
            {!isEditing && (
              <Tooltip title="Edit Offer">
                <IconButton
                  className="modal-header-button"
                  size="small"
                  onClick={() => {
                    setIsEditing(true);
                    setApiError('');
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isEditing && onPublish && data?.status !== 'PUBLISHED' && (
              <Tooltip title={data?.is_active !== false ? 'Publish Offer' : 'Activate offer to publish'}>
                <span>
                  <IconButton
                    className="modal-header-button"
                    size="small"
                    sx={{ ml: 1 }}
                    disabled={data?.is_active === false}
                    onClick={() => data?.is_active !== false && setOpenPublishConfirm(true)}
                  >
                    <PublishIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={data?.status === 'PUBLISHED' ? 'Published offers cannot be deleted' : 'Delete'}>
              <span>
                <IconButton
                  className="modal-header-button"
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => data?.status !== 'PUBLISHED' && setOpenDeleteConfirm(true)}
                  disabled={data?.status === 'PUBLISHED'}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton className="modal-header-button" size="small" sx={{ ml: 1 }} onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          {isEditing ? (
            <OfferForm
              formData={form}
              modalType={offerType}
              onFormChange={handleChange}
              errors={formErrors}
            />
          ) : (
            <>
              {/* View Mode - Original Display */}
              {/* Headline */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Offer Headline
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{data.headline}</Typography>
              </Box>

          {/* Description */}
          {shouldDisplayValue(data.description) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Offer Description
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>{data.description}</Typography>
            </Box>
          )}

          {/* Content */}
          {/* {shouldDisplayValue(data.content) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Offer Content
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>{data.content}</Typography>
            </Box>
          )} */}

          {/* Lease Fields */}
          {(offerType === 'lease' || offerType === 'leaseFinance') && (
            <>
              {shouldDisplayValue(data.lease_terms?.[0]?.monthly_payment) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Lease Amount
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    ${data.lease_terms?.[0]?.monthly_payment}
                  </Typography>
                </Box>
              )}

              {shouldDisplayValue(data.lease_terms?.[0]?.lease_term_months) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Lease Term (Months)
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {data.lease_terms?.[0]?.lease_term_months} months
                  </Typography>
                </Box>
              )}

              {shouldDisplayValue(data.lease_terms?.[0]?.due_at_signing) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {data.lease_terms?.[0]?.lease_down_label || 'Down'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    ${data.lease_terms?.[0]?.due_at_signing}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Finance Fields */}
          {(offerType === 'finance' || offerType === 'leaseFinance') && (
            <>
              {shouldDisplayValue(data.finance_terms?.apr) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Finance Rate (APR)
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {data.finance_terms?.apr}%
                  </Typography>
                </Box>
              )}

              {shouldDisplayValue(data.finance_terms?.term_months) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Finance Term (Months)
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {data.finance_terms?.term_months} months
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Vehicle Information */}
          {(shouldDisplayValue(data.vehicle_year) || shouldDisplayValue(data.vehicle_make) || shouldDisplayValue(data.vehicle_model) || shouldDisplayValue(data.trim)) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Vehicle Information
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {[data.vehicle_year, data.vehicle_make, data.vehicle_model, data.trim]
                  .filter(shouldDisplayValue)
                  .join(' ')}
              </Typography>
            </Box>
          )}

          {/* Dates */}
          {(shouldDisplayValue(data.start_date) || shouldDisplayValue(data.end_date)) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Offer Period
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {shouldDisplayValue(data.start_date) && formatDateForDisplay(data.start_date)}
                {shouldDisplayValue(data.start_date) && shouldDisplayValue(data.end_date) && ' - '}
                {shouldDisplayValue(data.end_date) && formatDateForDisplay(data.end_date)}
              </Typography>
            </Box>
          )}

          {/* Additional Fields */}
          {shouldDisplayValue(data.offer_url) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Offer URL
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {data.offer_url}
              </Typography>
            </Box>
          )}

          {/* Offer Image */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Offer Image
            </Typography>
            {shouldDisplayValue(data.offer_image_url) ? (
            <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <ImagePreview 
                imageUrl={data.offer_image_url} 
                isEditing={false}
                onClick={() => window.open(data.offer_image_url, '_blank')}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline'
                    }
                  }}
                  onClick={() => window.open(data.offer_image_url, '_blank')}
                  title={data.offer_image_url}
                >
                  {truncateUrl(data.offer_image_url, 60)}
                </Typography>
                <Typography variant="caption" color="text.primary">
                  Click image or URL to open in new tab
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <ImagePreview 
                imageUrl="" 
                isEditing={false} 
              />
              <Typography variant="caption" color="text.primary" sx={{ mt: 1, display: 'block' }}>
                No image URL provided
              </Typography>
            </Box>
          )}
          </Box>

          {shouldDisplayValue(data.disclaimer) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Disclaimer
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {data.disclaimer}
              </Typography>
            </Box>
          )}
            </>
          )}
        </Box>

        <Divider />

        {/* Footer action bar */}
        {isEditing && (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
              zIndex: 9,
              flexShrink: 0,
              gap: 2,
            }}
          >
            <ButtonMAIQ
              variant="outlined"
              onClick={() => {
                setIsEditing(false);
                setFormErrors({}); // Clear errors when canceling
                setApiError('');
              }}
            >
              Cancel
            </ButtonMAIQ>
            {saving ? (
              <Skeleton
                variant="rectangular"
                width={140}
                height={36}
                sx={{
                  borderRadius: 1,
                  bgcolor: theme.palette.action.hover,
                }}
              />
            ) : (
              <ButtonMAIQ
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                Save Changes
              </ButtonMAIQ>
            )}
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Modal */}
      <ConfirmationDialogMAIQ
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await onDelete();
            setOpenDeleteConfirm(false);
          } finally {
            setIsDeleting(false);
          }
        }}
        message={`Are you sure you want to delete this ${getOfferTypeDisplayName(offerType)} offer?`}
        loading={isDeleting}
      />

      {/* Publish Confirmation Modal */}
      {onPublish && (
        <ConfirmationDialogMAIQ
          open={openPublishConfirm}
          onClose={() => !isPublishing && setOpenPublishConfirm(false)}
          onConfirm={async () => {
            setIsPublishing(true);
            setApiError('');
            try {
              await onPublish();
              setOpenPublishConfirm(false);
            } catch (err) {
              setApiError(err instanceof Error ? err.message : 'Failed to publish offer. Please try again.');
            } finally {
              setIsPublishing(false);
            }
          }}
          title="Publish Offer"
          message={`Are you sure you want to publish this ${getOfferTypeDisplayName(offerType)} offer? Once published, it cannot be edited directly.`}
          loading={isPublishing}
          confirmLabel="Publish"
          loadingLabel="Publishing..."
        />
      )}

      {/* Save published offer – creates new copy confirmation */}
      <ConfirmationDialogMAIQ
        open={openSavePublishedConfirm}
        onClose={() => !saving && setOpenSavePublishedConfirm(false)}
        onConfirm={async () => {
          const success = await performSave();
          if (success) {
            setOpenSavePublishedConfirm(false);
          }
        }}
        title="Save as new copy"
        message="This offer is already published. Saving will create a new copy of the offer. Do you want to save and create a new copy of this offer?"
        loading={saving}
        confirmLabel="Save"
        loadingLabel="Saving..."
      />
    </ModalMAIQ>
  );
};

export default OfferDetailsModal; 